import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import type { LabelListContent } from "../../../api/content/authoring/processContentDecoders";
import { newItemId } from "../../../shared/references/itemIds";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_COMPACT_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import type { CommitBuilder } from "../../changeSets/changeSetChoice";
import AddToChangeSet from "../draft/AddToChangeSet";
import DefinitionPanel from "../draft/DefinitionPanel";
import FieldProblems from "../draft/FieldProblems";
import { itemEditAt, replacingTargetOf, type ItemTarget } from "../draft/itemEdits";
import { useDefinitionCheck } from "../draft/useDefinitionCheck";
import { useUnsavedDraft } from "../draft/useUnsavedDraft";
import SimilarItemsPanel from "../similar/SimilarItemsPanel";
import Section from "../../../ui/forms/Section";
import LabelFields from "./LabelFields";
import Field from "../../../ui/forms/Field";

interface LabelListBase {
  readonly reference: string;
  readonly itemId: string;
  readonly content: LabelListContent;
}

const NEW_LABEL_LIST: LabelListContent = { kind: "label_list", name: "", description: "", labels: [] };

/** The current hand-authored label-list format, checked and reviewed like an artifact. */
export default function LabelListForm({ projectId, base }: { projectId: string; base: LabelListBase | null }) {
  const { t } = useTranslation();
  const [content, setContent] = useState(() => base?.content ?? NEW_LABEL_LIST);
  // Row identities stay with their editors when labels move or their keys change.
  const [rowIds, setRowIds] = useState(() => content.labels.map(() => newItemId()));
  const [target] = useState<ItemTarget>(() => replacingTargetOf(base));

  const { connectSaver, markSaved } = useUnsavedDraft(content);
  const check = useDefinitionCheck(projectId, content, base === null ? null : target.itemId);
  const commits = useCallback((): readonly CommitBuilder[] => [async (head) => [await itemEditAt(projectId, head, target, content)]], [projectId, target, content]);
  const move = (index: number, offset: number) => {
    const to = index + offset;
    const order = content.labels.map((_, at) => at);
    order.splice(index, 1);
    order.splice(to, 0, index);
    setContent({ ...content, labels: order.flatMap((at) => content.labels[at] ?? []) });
    setRowIds(order.flatMap((at) => rowIds[at] ?? []));
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <div className="space-y-4">
        <Section title={t("authoring.labelList.about")}>
          <Field label={t("authoring.labelList.name")}>
            {(control) => (
              <input {...control} className={FORM_COMPACT_CONTROL_CLASS} value={content.name} maxLength={200} onChange={(event) => { setContent({ ...content, name: event.target.value }); }} />
            )}
          </Field>
          <FieldProblems check={check} path="name" />
          <Field label={t("authoring.labelList.description")}>
            {(control) => (
              <textarea {...control} className={FORM_COMPACT_CONTROL_CLASS} rows={3} maxLength={20000} value={content.description} onChange={(event) => { setContent({ ...content, description: event.target.value }); }} />
            )}
          </Field>
          <FieldProblems check={check} path="description" />
        </Section>
        <Section title={t("authoring.labelList.labels")}>
          <FieldProblems check={check} path="labels" />
          {content.labels.map((label, index) => (
            <LabelFields
              key={rowIds[index]}
              label={label}
              index={index}
              count={content.labels.length}
              check={check}
              onChange={(updated) => { setContent({ ...content, labels: content.labels.map((entry, at) => at === index ? updated : entry) }); }}
              onMove={(offset) => { move(index, offset); }}
              onRemove={() => {
                setContent({ ...content, labels: content.labels.filter((_, at) => at !== index) });
                setRowIds(rowIds.filter((_, at) => at !== index));
              }}
            />
          ))}
          <AppButton variant="secondary" size="sm" onClick={() => {
            setContent({ ...content, labels: [...content.labels, { key: "", name: "", description: "", guidelines: "", examples: [], ontology_terms: [] }] });
            setRowIds([...rowIds, newItemId()]);
          }}>{t("authoring.labelList.addLabel")}</AppButton>
        </Section>
      </div>
      <div className="space-y-4">
        <SimilarItemsPanel projectId={projectId} kind="label_list" name={content.name} description={content.description} excludeItemId={base?.itemId} />
        <DefinitionPanel content={content} base={base?.content ?? null} check={check} />
        <AddToChangeSet
          ref={connectSaver}
          projectId={projectId}
          defaultTitle={t(base === null ? "authoring.labelList.addTitle" : "authoring.labelList.editTitle", { name: content.name || t("authoring.labelList.unnamed") })}
          commits={commits}
          blockedBy={check.status !== "checked" || check.problems.length > 0 ? t("authoring.labelList.blocked") : null}
          onSaved={markSaved}
        />
      </div>
    </div>
  );
}
